import React, { useState, useCallback } from 'react';
import { Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles, MenuItem } from '@material-ui/core';
import qs from 'qs';
import { useGetCustomersPaginatedQuery } from 'src/reactQuery/customers/queries/useGetCustomersPaginated';
import { useSearchCustomersQuery } from 'src/reactQuery/customers/queries/useSearchCustomers';
import Modal from '../../../component/Modal/Modal';
import NoxTable from '../../../component/Table';
import {
  downloadFileAction,
  getGeneratedPassword,
  createCustomerAction,
  updateCustomerAction,
  deleteCustomerAction
} from '../../../actions/customersAction';
import InnerHeader from '../../../component/InnerHeader';
import Loader from '../../../component/Loader/Loader';
import CButton from '../../../component/Button';
import { CStandardInput } from '../../../component/Input';
import Copy from '../../../component/Copy';
import formatDate from '../../../helpers/formatDate';
import Eye from '../../../asset/hide.svg';
import OpenEye from '../../../asset/eye-open.svg';
import copyIcon from '../../../asset/copy.svg';
import { DefaultSelect } from '../../../component/Select';
import { useDebounce } from '../../../hooks/useDebounce';

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  },
  title: {
    fontFamily: 'Poppins',
    fontSize: '24px',
    fontWeight: 700
  }
});

const modalStyle = {
  '& .MuiDialogContent-root': {
    display: 'flex',
    flexDirection: 'column',
    height: '290px',
    justifyContent: 'space-evenly',
    paddingTop: '0px'
  },
  width: '400px'
};

const copyIconStyle = {
  cursor: 'pointer',
  marginRight: '5px',
  width: '18px'
};

const Customers = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [blockReason, setBlockReason] = useState('Reason');
  const [edit, setEdit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const classes = useStyles();
  const history = useHistory();
  const urlLocation = useLocation();
  const dispatch = useDispatch();

  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  const currentProject = useSelector(({ project }) => project.currentProject);
  const generatedPassword = useSelector(({ customer }) => customer.password);

  const useGetSearchString = useCallback(() => {
    const { search } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    return search || '';
  }, [urlLocation.search]);

  const searchString = useGetSearchString();

  const onFilterChange = (status) => {
    params.status = status;

    history.push({
      pathname: urlLocation.pathname,
      search: qs.stringify(params)
    });
  };

  const {
    data: customerData,
    isLoading,
    refetch: refetchCustomers
  } = useGetCustomersPaginatedQuery({
    customerId: currentProject._id,
    page: params.page,
    pageSize: params.pageSize,
    status: params.status
  });

  const { data: searchCustomerData, refetch: refetchSearch } = useSearchCustomersQuery(
    currentProject._id,
    searchString
  );

  useDebounce(() => refetchSearch(), 500, [searchString]);

  const showModalHandler = () => {
    setShowAddModal(true);
    onGenerate();
  };

  const onCancel = () => {
    setShowModal(false);
    setBlockReason('');
    setEdit(false);
  };

  const onAddCancel = () => {
    setShowAddModal(false);
  };

  const onDownloadCsv = () => {
    dispatch(downloadFileAction());
  };

  const onCustomerDelete = () => {
    selectedRowKeys.forEach((index) => {
      const item = filteredData.filter((item) => item.key === index)[0];

      dispatch(deleteCustomerAction(item.id, refetchCustomers()));
    });

    setSelectedRowKeys([]);
  };

  const onCustomerBlock = () => {
    selectedRowKeys.forEach((index) => {
      const item = filteredData.filter((item) => item.key === index)[0];

      const selectedCustomer = { ...customers[index] };

      if (selectedCustomer.status) {
        selectedCustomer.status = 'Blocked';
        selectedCustomer.reason = blockReason;
      } else {
        selectedCustomer.status = 'Blocked';
        selectedCustomer.reason = blockReason;
      }

      dispatch(updateCustomerAction(item.id, selectedCustomer, refetchCustomers()));
    });

    setSelectedRowKeys([]);
    setShowModal(false);
    onCancel();
  };

  const onCustomerUnblock = () => {
    selectedRowKeys.forEach((index) => {
      const item = filteredData.filter((item) => item.key === index)[0];

      let selectedCustomer = { ...customers[index] };

      if (selectedCustomer.status) {
        selectedCustomer = {
          ...selectedCustomer,
          reason: '',
          status: 'Active'
        };
      } else {
        selectedCustomer = {
          ...selectedCustomer,
          status: 'Active'
        };
      }

      dispatch(updateCustomerAction(item._id, selectedCustomer, refetchCustomers()));
    });

    setSelectedRowKeys([]);
    setShowModal(false);
    onCancel();
  };

  const clearInput = () => {
    setFullName('');
    setEmail('');
    setPassword('');
    setShowPassword(false);
  };

  const onSave = async () => {
    dispatch(createCustomerAction(fullName, email, password));
    clearInput();
    setShowAddModal(false);
  };

  const onGenerate = () => {
    dispatch(getGeneratedPassword());
    setPassword(generatedPassword);
  };

  const openDetail = (item) => {
    history.push({
      pathname: `/authentication/customers/${item.id}`
    });
  };

  const mapToTable = (customers) => {
    const data = [];

    if (!customers || customers.length === 0) {
      return data;
    }

    customers.forEach(({ _id, fullName, username, email, createdAt, status }, index) => {
      data.push({
        email,
        fullName: fullName || username || '',
        id: _id,
        key: index,
        registeredAt: formatDate(createdAt),
        status: status || 'Active'
      });
    });

    const sortedData = data.sort((itemA, itemB) => {
      const dateA = new Date(itemA?.registeredAt)?.getTime();
      const dateB = new Date(itemB?.registeredAt)?.getTime();
      return dateA >= dateB ? -1 : 1;
    });

    return sortedData;
  };

  // const filterData = (data) => {
  //   if (selectedFilter === 'All') {
  //     return data;
  //   }
  //   return data.filter((item) => item.status === selectedFilter);
  // };

  const columns = [
    {
      dataIndex: 'id',
      key: 'id',
      render: (id) => {
        return <Copy text={id} />;
      },
      sorter: (a, b) => a.id.localeCompare(b.id),
      title: <div style={{ maxWidth: '37px' }}>ID</div>
    },
    {
      dataIndex: 'fullName',
      key: 'fullName',
      render: (fullName) => {
        return <Copy text={fullName} />;
      },
      sorter: (a, b) => a.fullName.toString().localeCompare(b.fullName),
      title: 'Full Name'
    },
    {
      dataIndex: 'email',
      key: 'email',
      render: (email) => {
        return <Copy text={email} />;
      },
      sorter: (a, b) => a.email.toString().localeCompare(b.email),
      title: 'Email'
    },
    {
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: (registeredAt) => {
        return <Copy text={registeredAt} />;
      },
      sorter: (a, b) => new Date(a.registeredAt) - new Date(b.registeredAt),
      title: 'Registered At'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let style = {};
        switch (status) {
          case 'Active':
            style = {
              backgroundColor: 'rgba(65, 204, 156, 0.15)',
              border: 'none',
              color: '#41CC9C',
              width: '90%'
            };
            break;
          case 'Unknown':
            style = {
              backgroundColor: 'rgba(155, 160, 173, 0.15)',
              border: 'none',
              color: '#9BA0AD',
              width: '90%'
            };
            break;
          case 'Blocked':
            style = {
              backgroundColor: 'rgba(255, 71, 108, 0.15)',
              border: 'none',
              color: '#FF476C',
              width: '90%'
            };
            break;
          default:
            break;
        }
        return (
          <Tag key={status} style={style}>
            {status}
          </Tag>
        );
      },
      sorter: (a, b) => a.status.localeCompare(b.status),
      title: 'Status'
    },
    {
      key: 'edit',
      render: (item) => {
        return <Tag onClick={() => openDetail(item)}>edit</Tag>;
      }
    }
  ];

  const actions = [
    {
      onClick: showModalHandler,
      title: 'Add User',
      type: 'primary'
    },
    {
      onClick: onDownloadCsv,
      title: 'Download .CSV',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift(
      {
        onClick: onCustomerDelete,
        title: 'Delete'
      },
      {
        onClick: () => setShowModal(true),
        title: 'Block'
      },
      {
        onClick: () => onCustomerUnblock(),
        title: 'Unblock'
      }
    );
  }

  const reasons = ['Offensive content', 'Taken From Web', 'Fake Item'];

  const filterList = ['All', 'Active', 'Blocked'];
  const customers = useSelector(({ customer }) => customer.customers);
  const filteredData = mapToTable(searchString ? searchCustomerData : customerData?.users);

  if (!currentProject) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader
        subtitle={
          searchString && searchCustomerData
            ? searchCustomerData.length
            : customerData?.paging?.total
        }
        title="Customers"
      />
      <NoxTable
        actions={actions}
        columns={columns}
        data={filteredData}
        filterList={[filterList]}
        loading={isLoading}
        onFilterChange={onFilterChange}
        searchBy={['fullName', 'email', 'id']}
        selectedFilter={[params.status || 'All']}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={
          searchString && searchCustomerData
            ? Math.round(searchCustomerData.length)
            : customerData?.paging?.total
        }
      />
      <Modal
        footerActions={
          <>
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onCustomerBlock} type="primary" xstyle={{ height: '40px' }}>
              Block
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={showModal}
        styles={{
          '& .MuiDialogContent-root': {
            paddingBottom: '24px'
          },
          width: '400px'
        }}
        titleContent={<span className="modal-title">Select a Reason</span>}
      >
        <DefaultSelect onSelect={(reason) => setBlockReason(reason)} value={blockReason}>
          <MenuItem key="reason" value="Reason">
            Reason
          </MenuItem>
          {reasons.map((reason) => (
            <MenuItem key={reason} value={reason}>
              {reason}
            </MenuItem>
          ))}
        </DefaultSelect>
      </Modal>
      <Modal
        footerActions={
          <>
            <CButton onClick={onAddCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onSave} type="primary" xstyle={{ height: '40px' }}>
              Add
            </CButton>
          </>
        }
        handleClose={onAddCancel}
        isModalOpen={showAddModal}
        styles={modalStyle}
        titleContent={<p className="modal-title">Add User</p>}
      >
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-user-full-name"
          id="standard-input-user-full-name"
          label="Full Name"
          onChange={(e) => setFullName(e.target.value)}
          size="large"
          value={fullName || ''}
          width="true"
        />
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-user-email"
          id="standard-input-user-email"
          label="Email"
          onChange={(e) => setEmail(e.target.value)}
          size="large"
          value={email || ''}
          width="true"
        />
        <div className={classes.passwordWrapper}>
          <CStandardInput
            adornment={
              !edit ? (
                <>
                  <img
                    alt="copy password icon"
                    onClick={() => {
                      navigator.clipboard.writeText(password);
                    }}
                    src={copyIcon}
                    style={copyIconStyle}
                  />
                  <img
                    alt="hide password icon"
                    onClick={() => {
                      setShowPassword((s) => !s);
                    }}
                    src={showPassword ? OpenEye : Eye}
                    style={{ width: '18px' }}
                  />
                </>
              ) : null
            }
            disabled={!!edit}
            disableUnderline
            formclasses={{ root: classes.root }}
            htmlFor="standard-input-user-password"
            id="standard-input-user-password"
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            size="large"
            type={showPassword ? 'text' : 'password'}
            value={password || ''}
            width="true"
          />
          {!edit ? (
            <CButton
              onClick={onGenerate}
              type="primary"
              xstyle={{
                borderRadius: '12px',
                margin: '16px 0px 0px 16px'
              }}
            >
              Generate
            </CButton>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default Customers;
